import { useEffect, useMemo, useRef, useState } from 'react';
import { googleLogin } from '../api/auth';
import type { AuthUser } from '../api/auth';

const GOOGLE_GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

let googleScriptPromise: Promise<void> | null = null;

function loadGoogleIdentityScript(): Promise<void> {
  if (window.google?.accounts?.id) {
    return Promise.resolve();
  }

  if (googleScriptPromise !== null) {
    return googleScriptPromise;
  }

  const scriptPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="${GOOGLE_GIS_SCRIPT_SRC}"]`,
    );
    const script = existingScript ?? document.createElement('script');

    const handleLoad = () => {
      if (window.google?.accounts?.id) {
        resolve();
        return;
      }
      reject(new Error('Google sign-in script loaded without the expected API.'));
    };

    const handleError = () => {
      reject(new Error('Unable to load Google sign-in right now.'));
    };

    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });

    if (!existingScript) {
      script.src = GOOGLE_GIS_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    } else if (window.google?.accounts?.id) {
      resolve();
    }
  }).catch((error: unknown) => {
    googleScriptPromise = null;
    throw error;
  });

  googleScriptPromise = scriptPromise;
  return scriptPromise;
}

interface AuthScreenProps {
  onAuthenticated: (user: AuthUser) => Promise<void>;
  retryNonce?: number;
}

export function AuthScreen({ onAuthenticated, retryNonce = 0 }: AuthScreenProps) {
  const [isInitializing, setIsInitializing] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const googleButtonRef = useRef<HTMLDivElement | null>(null);
  const clientId =
    typeof import.meta.env.VITE_GOOGLE_CLIENT_ID === 'string'
      ? import.meta.env.VITE_GOOGLE_CLIENT_ID.trim()
      : '';

  const title = useMemo(() => {
    return 'Sign in to TraderPro';
  }, []);

  const subtitle = useMemo(() => {
    return 'Continue with Google to start your session.';
  }, []);

  useEffect(() => {
    let isActive = true;

    async function initializeGoogleAuth() {
      setIsInitializing(true);
      if (clientId.length === 0) {
        setErrorMessage('Google sign-in is unavailable because VITE_GOOGLE_CLIENT_ID is not set.');
        setIsInitializing(false);
        return;
      }

      try {
        await loadGoogleIdentityScript();
        if (!isActive) {
          return;
        }

        const buttonElement = googleButtonRef.current;
        const googleIdentity = window.google?.accounts?.id;
        if (!buttonElement || !googleIdentity) {
          setErrorMessage('Google sign-in is unavailable. Please refresh and try again.');
          setIsInitializing(false);
          return;
        }

        googleIdentity.initialize({
          client_id: clientId,
          callback: (response) => {
            void (async () => {
              if (!isActive) {
                return;
              }

              if (typeof response.credential !== 'string' || response.credential.length === 0) {
                setErrorMessage('Google sign-in did not return a valid credential.');
                return;
              }

              setIsSubmitting(true);
              setErrorMessage(null);

              try {
                const user = await googleLogin(response.credential);
                await onAuthenticated(user);
              } catch (error) {
                const message = error instanceof Error ? error.message : 'Google authentication failed.';
                if (isActive) {
                  setErrorMessage(message);
                }
              } finally {
                if (isActive) {
                  setIsSubmitting(false);
                }
              }
            })();
          },
        });

        buttonElement.innerHTML = '';
        googleIdentity.renderButton(buttonElement, {
          type: 'standard',
          shape: 'pill',
          theme: 'filled_black',
          text: 'signin_with',
          size: 'large',
          width: 320,
        });

        setErrorMessage(null);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unable to initialize Google sign-in.';
        if (isActive) {
          setErrorMessage(message);
        }
      } finally {
        if (isActive) {
          setIsInitializing(false);
        }
      }
    }

    void initializeGoogleAuth();

    return () => {
      isActive = false;
    };
  }, [clientId, onAuthenticated, retryNonce]);

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-x-hidden bg-gradient-to-b from-surface-base via-surface-muted to-surface-base px-4 py-10">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(122,170,209,0.22),transparent_44%),radial-gradient(circle_at_bottom_right,rgba(86,121,162,0.2),transparent_40%)]" />
      <div className="relative w-full max-w-md rounded-3xl border border-border-strong/70 bg-surface-elevated/95 p-7 shadow-[0_26px_52px_rgba(2,6,18,0.72)] ring-1 ring-slate-200/5 backdrop-blur-sm">
        <div className="inline-flex items-center rounded-full border border-brand-primary/45 bg-brand-primary/20 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-brand-accent">
          TraderPro
        </div>
        <h1 className="mt-4 text-3xl font-semibold tracking-tight text-white">{title}</h1>
        <p className="mt-2 text-sm leading-relaxed text-slate-200">{subtitle}</p>

        <div className="mt-7 rounded-2xl border border-border-subtle bg-surface-base/80 p-5 shadow-inner shadow-slate-950/40">
          <div
            className={`mx-auto min-h-12 w-full max-w-[320px] ${isSubmitting ? 'hidden' : ''}`}
            ref={googleButtonRef}
          />
          {isInitializing ? (
            <p className="mt-3 text-center text-xs text-slate-300">Loading Google sign-in...</p>
          ) : null}
          {isSubmitting ? (
            <div className="auth-status-fade mt-3 flex items-center justify-center gap-2 text-sm font-medium text-slate-100">
              <span
                aria-hidden="true"
                className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-slate-500 border-t-slate-100"
              />
              <p>Signing in with Google...</p>
            </div>
          ) : null}
        </div>

        {errorMessage ? <p className="mt-4 text-sm text-rose-200">{errorMessage}</p> : null}
      </div>
    </div>
  );
}
